import React, { useRef, useMemo } from 'react';
import classNames from 'classnames';
import { round } from 'lodash';
import { Box, Card, Typography } from '@mui/material';
import { mapDegreesCardinal, getTop3Swells } from '@surfline/web-common';
import { SwellArrowIcon } from '@wavetrak/theme';

import ObservedBadge from 'components/ObservedBadge';
import type { Units } from 'types/units';
import type { SurfTooltipData, SwellsTooltipData } from 'types/graphs';
import type { SurfObservation } from 'types/surf';
import type { SwellPoint } from 'types/spot';
import formatWaveHeight from 'utils/formatWaveHeight';
import conditionIsFlat from 'utils/conditionIsFlat';
import { roundSurfHeight } from 'utils/roundUnits';

import styles from './SurfTooltip.module.scss';

export interface SurfTooltipProps {
  activeSurf: SurfTooltipData;
  activeSwells: SwellsTooltipData;
  conditionsRating: string;
  graphWidth: number;
  isMobile: boolean;
  units: Units;
  observationData?: SurfObservation[];
}

export const SURF_CARD_PADDING = 20;

const SwellRow: React.FC<{ index: number; swell: SwellPoint; units: Units }> = ({
  index,
  swell,
  units,
}) => {
  const swellDisplayHeight = useMemo(
    () => (swell.height >= 10 ? round(swell.height, 0) : round(swell.height, 1)),
    [swell.height],
  );
  const directionCardinal = useMemo(() => mapDegreesCardinal(swell.direction), [swell.direction]);
  const directionDegrees = useMemo(() => `${round(swell.direction)}°`, [swell.direction]);

  return (
    <Box className={classNames(styles.swellRow, { [styles.swellRowPrimary]: index === 0 })}>
      <Typography className={styles.swellHeight} variant="caption1">
        {`${swellDisplayHeight}${formatWaveHeight(units?.swellHeight || '', true)}`}
      </Typography>
      <Typography className={styles.swellPeriod} variant="caption1">
        {`${round(swell.period)}s`}
      </Typography>
      <Box className={styles.swellDirectionContainer}>
        <Box className={styles.swellIconContainer}>
          <SwellArrowIcon size="small" rotation={swell.direction - 180} />
        </Box>
        <Typography className={styles.swellDirection} variant="callout2" component="span">
          {directionCardinal}
        </Typography>
        <Typography className={styles.directionDegrees} variant="callout2" component="span">
          {directionDegrees}
        </Typography>
      </Box>
    </Box>
  );
};

const SurfTooltip: React.FC<SurfTooltipProps> = ({
  activeSurf,
  activeSwells,
  conditionsRating,
  graphWidth,
  isMobile,
  units,
  observationData = [],
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null);

  const positionStyles = useMemo(() => {
    const cardWidth = cardRef?.current?.offsetWidth || 0;
    let leftPosition = 0;
    if (activeSurf?.leftPosition >= 0) {
      leftPosition = Math.min(
        isMobile ? activeSurf.leftDayPosition : activeSurf.leftPosition - SURF_CARD_PADDING,
        graphWidth - cardWidth,
      );
    }
    return { left: Math.max(leftPosition, 0) };
  }, [activeSurf?.leftDayPosition, activeSurf?.leftPosition, graphWidth, isMobile]);

  const sortedTop3Swells = useMemo(() => {
    const validSwells = (activeSwells?.data?.swells || []).filter(
      (swell) => swell.height > 0 && swell.height !== null && swell.period > 0,
    );
    return getTop3Swells(validSwells) as SwellPoint[];
  }, [activeSwells?.data?.swells]);

  // Find an observation that matches this timestamp if it exists
  const matchingObservation = useMemo(() => {
    if (!activeSurf?.timestamp || observationData.length === 0) return null;

    // First try exact timestamp match
    const exactMatch = observationData.find(
      (obs) => obs.timestamp === activeSurf.timestamp && obs.surf !== null,
    );

    if (exactMatch) {
      return exactMatch;
    }

    // If no exact match, try looking for a close match (within 5 minutes = 300 seconds)
    const closeMatch = observationData.find(
      (obs) => Math.abs(obs.timestamp - (activeSurf.timestamp || 0)) < 300 && obs.surf !== null,
    );

    if (closeMatch) {
      return closeMatch;
    }

    return null;
  }, [activeSurf?.timestamp, observationData]);

  // Check if we're showing observed data
  const showObservedTag = useMemo(() => {
    // First check if we have a matching observation
    if (matchingObservation) {
      return true;
    }

    // Then check if the data is explicitly marked as observed
    if (activeSurf?.data?.isObserved) {
      return true;
    }

    return false;
  }, [activeSurf, matchingObservation]);

  const surfData = useMemo(
    () => matchingObservation?.surf || activeSurf?.data?.surf,
    [activeSurf, matchingObservation],
  );

  const isFlat = useMemo(
    () => conditionIsFlat(surfData?.max, units?.waveHeight),
    [surfData, units],
  );

  const swellRows = useMemo(
    () =>
      sortedTop3Swells.map((swell, index) => (
        <SwellRow
          index={index}
          key={`swell-${swell.height}-${swell.direction}-${swell.period}`}
          swell={swell}
          units={units}
        />
      )),
    [sortedTop3Swells, units],
  );

  const surfHeight = useMemo(() => {
    if (isFlat) return 'Flat';
    const roundedMinHeight = roundSurfHeight(surfData?.min, units?.waveHeight);
    const roundedMaxHeight = roundSurfHeight(surfData?.max, units?.waveHeight);
    return `${roundedMinHeight}-${roundedMaxHeight}`;
  }, [isFlat, surfData, units?.waveHeight]);

  if (!activeSurf?.data?.surf && !matchingObservation?.surf) return null;

  return (
    <Box className={styles.surfTooltipContainer}>
      <Card
        className={styles.tooltipCard}
        data-testid="surf-tooltip"
        ref={cardRef}
        style={positionStyles}
      >
        <Box className={styles.surfHeightSection}>
          <Box className={styles.sectionHeader}>
            <Typography component="h3" variant="caption2" className={styles.sectionLabel}>
              Surf Height
            </Typography>
          </Box>
          <Box className={styles.waveHeightContent}>
            <Box className={styles.waveHeightRow}>
              <span>
                <Typography component="span" variant="title3">
                  {surfHeight}
                </Typography>
                {!isFlat && units?.waveHeight && (
                  <Typography component="span" variant="callout1">
                    {formatWaveHeight(units.waveHeight, true)}
                    {surfData?.plus && <>+</>}
                  </Typography>
                )}
              </span>
              {showObservedTag && (
                <div className={styles.observedBadge}>
                  <ObservedBadge />
                </div>
              )}
            </Box>
            <Typography className={styles.descriptiveHeight} variant="caption1">
              {surfData.humanRelation || conditionsRating || 'Shoulder to head high'}
            </Typography>
          </Box>
        </Box>
        <Box className={styles.swellSection}>
          <Box className={styles.sectionHeader}>
            <Typography component="h3" variant="caption2" className={styles.sectionLabel}>
              Swell
            </Typography>
          </Box>
          <Box className={styles.swellContent}>{swellRows}</Box>
        </Box>
      </Card>
    </Box>
  );
};

export default SurfTooltip;
